import { useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Send } from "@mui/icons-material";
import { useOktaAuth } from "@okta/okta-react";

const WSU_RED = "#A60F2D";

export const RemoveMFAFactor = ({ identifier }) => {
  const [state, setState] = useState({ isLoaded: false, error: null, data: null });
  const { authState } = useOktaAuth();

  useEffect(() => {
    const fetchFactors = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/user/factors/list`,
          {
            method: "POST",
            signal: AbortSignal.timeout(360000),
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: authState.accessToken.accessToken,
            },
            body: JSON.stringify({ username: identifier }),
          }
        );

        const data = await res.json();
        setState({ isLoaded: true, error: null, data });
      } catch (error) {
        setState({ isLoaded: true, error, data: null });
      }
    };

    fetchFactors();
  }, [identifier, authState.accessToken.accessToken]);

  const removeFactor = async (factorId, factorName) => {
    setState((prev) => ({ ...prev, isLoaded: false }));

    try {
      const res = await fetch(
        `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/user/factors/remove`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: authState.accessToken.accessToken,
          },
          body: JSON.stringify({ callerId: authState.accessToken.claims.sub, userId: identifier, factorId, factorName }),
        }
      );

      const result = await res.json();

      if (result.statusCode === 200) {
        setState({ isLoaded: true, error: null, data: result });
      } else {
        throw new Error(result.message || "Failed to remove MFA factor.");
      }
    } catch (error) {
      setState({ isLoaded: true, error, data: null });
    }
  };

  if (!state.isLoaded) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
      </Box>
    );
  }

  if (state.error) {
    return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Failed to load MFA factors</Typography>
          <Typography variant="body2">{state.error.message}</Typography>
        </Box>
      </TableContainer>
    );
  }

  return (
    <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>

      {/* ── Section: MFA Factors ── */}
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
          Modify MFA Factors
        </Typography>
        <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>{identifier}</Typography>
      </Box>

      <Divider sx={{ mx: 3 }} />

      <TableContainer sx={{ maxHeight: 440 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {["Factor Name", "Factor Type", "Factor ID", "Device Type", "Date Created", ""].map((h) => (
                <TableCell key={h} sx={{ backgroundColor: "#4D4D4D", color: "white", fontWeight: 700, fontSize: "0.8rem", letterSpacing: 0.5 }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {state.data?.factors?.map((factor) => {
              if (factor.factorType === "email") return null;

              const factorName = factor?.profile?.name || "";
              const deviceType = factor?.profile?.deviceType || "";

              return (
                <TableRow hover key={factor.id} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                  <TableCell><Typography variant="body2">{factorName}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{factor.factorType}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{factor.id}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{deviceType}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{factor.created}</Typography></TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      size="small"
                      endIcon={<Send />}
                      onClick={() => removeFactor(factor.id, factorName)}
                      sx={{
                        backgroundColor: WSU_RED,
                        color: "white",
                        fontWeight: 600,
                        "&:hover": { backgroundColor: "#8a0c25" },
                      }}
                    >
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </TableContainer>
  );
};
